import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useZipAutofill } from "@/lib/useZipAutofill";
import { LOCATION_TYPES } from "@/lib/constants";
import { MapPin, Plus, Trash2, Search, Building2, Loader2, Upload } from "lucide-react";

const emptyForm = {
  company: "", contact_name: "", contact_email: "", contact_phone: "",
  address1: "", city: "", state: "", zip: "", location_type: "commercial", notes: "",
};

export default function AddressBookDialog({ open, onOpenChange, side, prefill, onSelect }) {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const zipFill = useZipAutofill(setFormData);

  const fetchList = (q = "") => {
    setLoading(true);
    api.get(`/addresses${q ? `?search=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setList(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) {
      fetchList();
      setShowForm(false);
      setSearch("");
      if (prefill && (prefill.zip || prefill.city)) {
        setFormData({ ...emptyForm, ...prefill });
        setShowForm(true);
      } else {
        setFormData(emptyForm);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!formData.company.trim()) return toast.error("Enter a company / location name.");
    setSaving(true);
    try {
      await api.post("/addresses", formData);
      toast.success("Address saved to the book.");
      setShowForm(false);
      setFormData(emptyForm);
      fetchList(search);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/addresses/${id}`);
      setList((l) => l.filter((a) => a.id !== id));
      toast.success("Address removed.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const { data } = await api.post("/addresses/import", { csv_text: text });
      toast.success(`Imported ${data.imported} address${data.imported === 1 ? "" : "es"}${data.skipped ? ` · ${data.skipped} duplicates skipped` : ""}.`);
      fetchList(search);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="address-book-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <MapPin className="h-5 w-5 text-[hsl(var(--primary))]" /> Address Book
            {side && <span className="text-sm font-normal text-muted-foreground">· {side === "pickup" ? "Pickup" : "Destination"}</span>}
          </DialogTitle>
          <DialogDescription>Reusable, company-wide pickup & delivery locations.</DialogDescription>
        </DialogHeader>

        {!showForm ? (
          <>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="address-search"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); fetchList(e.target.value); }}
                  placeholder="Search company, city, ZIP, contact…"
                  className="h-9 pl-8"
                />
              </div>
              <Button data-testid="address-add-new" size="sm" onClick={() => { setFormData(prefill && (prefill.zip || prefill.city) ? { ...emptyForm, ...prefill } : emptyForm); setShowForm(true); }}>
                <Plus className="mr-1 h-4 w-4" /> New
              </Button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImportFile} data-testid="address-import-input" />
              <Button data-testid="address-import" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing}>
                {importing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Import CSV
              </Button>
            </div>

            <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
              {loading ? (
                <div className="flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : list.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No saved addresses yet. Add one or import a CSV to reuse later.</p>
              ) : (
                list.slice(0, 150).map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2.5" data-testid={`address-row-${a.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {a.company}
                        <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">{a.location_type}</span>
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[a.address1, a.city, a.state, a.zip].filter(Boolean).join(", ")}
                        {a.contact_name ? ` · ${a.contact_name}` : ""}
                      </div>
                      {a.saved_by && <div className="text-[10px] text-muted-foreground/70">saved by {a.saved_by}</div>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button data-testid={`address-use-${a.id}`} size="sm" variant="outline" onClick={() => { onSelect(a); onOpenChange(false); }}>Use</Button>
                      <button onClick={() => remove(a.id)} className="p-1.5 text-[hsl(var(--destructive))] hover:opacity-70" title="Delete" data-testid={`address-delete-${a.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {list.length > 150 && (
              <p className="text-center text-xs text-muted-foreground" data-testid="address-count-note">
                Showing 150 of {list.length}. Use search to find a specific address.
              </p>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="address-form-company" value={formData.company} onChange={(e) => setFormData((f) => ({ ...f, company: e.target.value }))} placeholder="Company / location name *" className="col-span-2 h-9" />
              <Input value={formData.contact_name} onChange={(e) => setFormData((f) => ({ ...f, contact_name: e.target.value }))} placeholder="Contact name" className="h-9" />
              <Input value={formData.contact_phone} onChange={(e) => setFormData((f) => ({ ...f, contact_phone: e.target.value }))} placeholder="Phone" className="h-9" />
              <Input value={formData.contact_email} onChange={(e) => setFormData((f) => ({ ...f, contact_email: e.target.value }))} placeholder="Email" className="col-span-2 h-9" />
              <Input value={formData.address1} onChange={(e) => setFormData((f) => ({ ...f, address1: e.target.value }))} placeholder="Street address" className="col-span-2 h-9" />
              <Input data-testid="address-form-zip" value={formData.zip} onChange={(e) => zipFill(e.target.value, { zipKey: "zip", cityKey: "city", stateKey: "state" })} placeholder="ZIP" className="h-9" />
              <Input data-testid="address-form-city" value={formData.city} onChange={(e) => setFormData((f) => ({ ...f, city: e.target.value }))} placeholder="City" className="h-9" />
              <Input data-testid="address-form-state" value={formData.state} onChange={(e) => setFormData((f) => ({ ...f, state: e.target.value }))} placeholder="State" maxLength={2} className="h-9" />
              <Select value={formData.location_type} onValueChange={(v) => setFormData((f) => ({ ...f, location_type: v }))}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{LOCATION_TYPES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button data-testid="address-form-save" onClick={save} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save Address
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
